import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { Fold } from '../../components/ui/Fold';
import { useAuth } from '../auth/AuthContext';
import { bindBarcode, listBarcodes, unbindBarcode } from './api';

/**
 * The barcodes on the packaging this part came in.
 *
 * Ours is the code on the label we printed; these are everyone else's — the reel
 * from the distributor, the box from the manufacturer. Recording one means an
 * unfamiliar packet is recognised the second time it is scanned instead of
 * sending somebody to the search box.
 */
export function ComponentBarcodes({ componentId }: { componentId: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const barcodes = useQuery({
    queryKey: ['components', componentId, 'barcodes'],
    queryFn: () => listBarcodes(componentId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['components', componentId, 'barcodes'] });

  const add = useMutation({
    mutationFn: () => bindBarcode(componentId, { value: value.trim(), source: 'manual' }),
    onSuccess: () => {
      setValue('');
      setError(null);
      void invalidate();
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => unbindBarcode(componentId, id),
    onSuccess: () => void invalidate(),
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const rows = barcodes.data ?? [];
  const canEdit = can('barcode.link');
  if (rows.length === 0 && !canEdit) return null;

  return (
    <Fold title={t('scan.barcodes')} count={barcodes.isLoading ? null : rows.length} className="barcodes">
      {rows.length === 0 && <p className="hint">{t('scan.noBarcodes')}</p>}

      <ul className="barcodes__list">
        {rows.map((row) => (
          <li key={row.id}>
            <code>{row.value}</code>
            <span className="hint">{row.symbology}</span>
            {canEdit && (
              <button type="button" onClick={() => remove.mutate(row.id)}>
                {t('common.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>

      {canEdit && (
        <form
          className="barcodes__add"
          onSubmit={(event) => {
            event.preventDefault();
            if (value.trim()) add.mutate();
          }}
        >
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={t('scan.barcodePlaceholder')}
            autoComplete="off"
          />
          <button type="submit" disabled={!value.trim() || add.isPending}>
            {t('scan.addBarcode')}
          </button>
        </form>
      )}

      {error && <p className="error">{t(`scan.errors.${error}`, t('scan.errors.unknown'))}</p>}
    </Fold>
  );
}
